import { Button, Modal } from "antd";
import { BellOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import { eventTracker } from "../../../utils/eventTracker";
import "./EnableBrowserPushModal.scss";

/**
 * Prompt for browser notifications. Always dismissible ("Më vonë" / X).
 * When the browser already blocked the site, we only show instructions —
 * the OS prompt cannot be re-opened from JS.
 */
export default function EnableBrowserPushModal({
  open,
  permission,
  supported,
  onEnable,
  onCloseWhenGranted,
  onDismiss,
}) {
  const { isDarkMode } = useSelector((state) => state.usersList);
  const granted = permission === "granted";
  const denied = permission === "denied";

  const handleEnable = async () => {
    eventTracker("BrowserPush", "click", "Enable from force modal");
    if (denied) {
      // Browser will not re-prompt; refresh in case user already changed settings.
      const next = await onEnable?.();
      if (next === "granted") {
        onCloseWhenGranted?.();
        return;
      }
      onDismiss?.();
      return;
    }
    const next = await onEnable?.();
    if (next === "granted") {
      onCloseWhenGranted?.();
    }
  };

  const handleDismiss = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    eventTracker("BrowserPush", "click", "Snooze force modal");
    onDismiss?.();
  };

  return (
    <Modal
      open={open && supported && !granted}
      title={null}
      footer={null}
      closable
      maskClosable
      keyboard
      onCancel={handleDismiss}
      centered
      width={480}
      zIndex={11000}
      getContainer={() => document.body}
      classNames={{
        root: `enable-browser-push-modal ${isDarkMode ? "dark" : ""}`,
      }}
      destroyOnHidden
    >
      <div className="enable-browser-push-modal__body">
        <div className="enable-browser-push-modal__icon" aria-hidden>
          <BellOutlined />
        </div>
        <h2>Aktivizo njoftimet në shfletues</h2>
        <p className="enable-browser-push-modal__lead">
          Që të mos humbasësh njoftime të rëndësishme (orar, aprovime, mesazhe),
          lejo njoftimet e FlexHR në këtë shfletues.
        </p>
        {typeof window !== "undefined" && window.isSecureContext === false ? (
          <p className="enable-browser-push-modal__lead">
            Je në <strong>HTTP</strong>. Hap{" "}
            <a href="https://portal1.flex.al">https://portal1.flex.al</a>.
          </p>
        ) : null}

        {denied ? (
          <div className="enable-browser-push-modal__denied">
            <p>
              Njoftimet janë <strong>bllokuar</strong> për këtë faqe nga
              shfletuesi. FlexHR nuk mund t’i rizgjojë nga këtu — ndrysho lejen
              në shfletues, pastaj rifresko.
            </p>
            <ol>
              <li>Kliko ikonën e kyçit / informacionit pranë adresës</li>
              <li>Gjej “Njoftimet” / “Notifications”</li>
              <li>Zgjidh “Lejo” / “Allow”</li>
              <li>Rifresko faqen (⌘⇧R)</li>
            </ol>
          </div>
        ) : (
          <ul className="enable-browser-push-modal__benefits">
            <li>
              <CheckCircleOutlined /> Njoftime në tray / Notification Center (si
              Gmail)
            </li>
            <li>
              <CheckCircleOutlined /> Mbërrin në kohë reale përmes WebSocket
            </li>
            <li>
              <CheckCircleOutlined /> Testo nga ikona e desktopit në header
            </li>
          </ul>
        )}

        <div className="enable-browser-push-modal__actions">
          {!denied ? (
            <MondayButton
              className="mondayButtonBlue enable-browser-push-modal__cta"
              onClick={handleEnable}
              hasIcon
              Icon={<BellOutlined />}
            >
              Aktivizo njoftimet
            </MondayButton>
          ) : null}
          {/* Plain antd Button — always clickable even if MondayButton styles fight the modal */}
          <Button
            type={denied ? "primary" : "default"}
            size="large"
            className="enable-browser-push-modal__later"
            onClick={handleDismiss}
          >
            {denied ? "Mbylle" : "Më vonë"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
